/**
 * D3 Sankey Diagram Renderer Composable
 *
 * Provides Sankey diagram visualization for HVAC airflow distribution.
 * Renders hierarchical airflow from AHU → Zones/VAVs → Spaces with proportional flow widths.
 *
 * @module useSankeyRenderer
 */

/**
 * Initialize Sankey renderer composable
 *
 * @returns {Object} Composable with renderSankeyDiagram function
 */
export function useSankeyRenderer() {
  /**
   * Render D3 Sankey diagram showing airflow distribution from AHU to spaces
   *
   * Creates a proportional flow diagram showing:
   * - AHU as root node
   * - VAVs and Zones as intermediate nodes
   * - Spaces as terminal nodes
   * - Link widths proportional to airflow values
   *
   * @param {Object} ahuData - AHU equipment data from hierarchy
   * @param {number} ahuData.id - AHU identifier
   * @param {string} ahuData.name - AHU name
   * @param {string} ahuData.brick_class - BRICK class (e.g., 'brick:AHU')
   * @param {Array} [ahuData.zone_sections] - Array of zone sections under this AHU
   * @param {Object} equipmentList - Reactive ref containing all equipment
   * @param {Object} localHierarchyData - Reactive ref containing site hierarchy
   * @param {string} containerId - DOM element ID for SVG container
   * @returns {Promise<void>}
   */
  const renderSankeyDiagram = async (ahuData, equipmentList, localHierarchyData, containerId = 'airflow-sankey') => {
    // Import D3 and sankey dynamically
    const d3 = await import('d3');
    const { sankey, sankeyLinkHorizontal } = await import('d3-sankey');

    // Clear previous diagram
    const container = document.getElementById(containerId);
    if (!container) {return;}
    container.innerHTML = '';

    // Prepare data for Sankey diagram
    const nodes = [];
    const links = [];
    const nodeMap = new Map();

    // Validate AHU data
    if (!ahuData) {
      container.innerHTML = '<div class="text-center text-grey-6 q-pa-md">No AHU data available</div>';
      return;
    }

    // Add the AHU as the root node
    nodes.push({
      id: ahuData.id,
      name: ahuData.name || `AHU ${ahuData.id}`,
      type: 'AHU',
      brick_class: ahuData.brick_class,
    });
    nodeMap.set(ahuData.id, 0);

    // Find VAVs connected to this AHU
    // Looking for VAVs connected to AHU

    const vavs = equipmentList.value.filter((eq) => {
      // Check if this is a VAV
      if (!(eq.brick_class === 'brick:Variable_Air_Volume_Box' ||
            eq.brick_class === 'brick:VAV' ||
            eq.equipment_type === 'VAV')) {
        return false;
      }

      // Parse properties field
      let properties = {};
      try {
        if (eq.properties) {
          properties = typeof eq.properties === 'string' ? JSON.parse(eq.properties) : eq.properties;
        }
      } catch (e) {
        // Failed to parse properties for equipment
      }

      const isConnected = properties.feeds_from === ahuData.id ||
             properties.ahu_id === ahuData.id ||
             eq.feeds_from === ahuData.id ||
             eq.ahu_id === ahuData.id ||
             eq.parent_equipment_id === ahuData.id;

      if (eq.name.includes('VAV')) {
        // VAV connection checked
      }

      return isConnected;
    });

    // VAVs found for this AHU

    // Add VAV nodes
    vavs.forEach((vav) => {
      // Parse properties for airflow value
      let properties = {};
      try {
        if (vav.properties) {
          properties = typeof vav.properties === 'string' ? JSON.parse(vav.properties) : vav.properties;
        }
      } catch (e) {
        // Failed to parse properties for VAV
      }

      nodes.push({
        id: vav.id,
        name: vav.name || `VAV ${vav.id}`,
        type: 'VAV',
        brick_class: vav.brick_class,
      });
      const vavIndex = nodes.length - 1;
      nodeMap.set(vav.id, vavIndex);

      // Link AHU to VAV with airflow from properties or estimated
      // Convert to number to ensure proper proportions in Sankey
      const airflow = Number(properties.airflow || properties.cfm || vav.airflow || vav.cfm || 500);
      links.push({ source: 0, target: vavIndex, value: airflow });
    });

    // Process zone sections from the selected AHU (if any)
    const zones = [];
    const zoneMap = new Map();

    // Check if this AHU has zone sections
    if (ahuData.zone_sections && Array.isArray(ahuData.zone_sections) && ahuData.zone_sections.length > 0) {
      // Processing zone sections for AHU

      ahuData.zone_sections.forEach((zone) => {
        // Get zone name from the zone data
        const zoneName = zone.name || `Zone ${zone.id}`;

        // Skip if this zone was already added (prevent duplicates)
        if (zoneMap.has(zone.id)) {
          // Skipping duplicate zone
          return;
        }

        // Add zone node to the diagram
        nodes.push({
          id: zone.id,
          name: zoneName,
          type: 'Zone',
          brick_class: zone.brick_class || 'AHU_Zone_Section',
        });
        const zoneIndex = nodes.length - 1;
        zoneMap.set(zone.id, zoneIndex);
        zones.push({ ...zone, index: zoneIndex });

        // Calculate airflow for this zone
        // Parse properties for airflow value
        let properties = {};
        try {
          if (zone.properties) {
            properties = typeof zone.properties === 'string' ? JSON.parse(zone.properties) : zone.properties;
          }
        } catch (e) {
          // Failed to parse properties for zone
        }

        // Get airflow value - convert to number to ensure proper proportions
        const airflowValue = Number(properties.airflow || properties.cfm || zone.airflow || zone.cfm || 1000);

        // Link AHU to Zone
        links.push({
          source: 0,
          target: zoneIndex,
          value: airflowValue,
        });

        // Added zone with airflow
      });
    }

    // Find spaces served (not zones - they're equipment sections)
    const spaces = [];
    const spaceIdSet = new Set(); // Track space IDs to prevent duplicates

    // Get spaces from hierarchy
    if (localHierarchyData.value && localHierarchyData.value.children) {
      const collectSpaces = (node) => {
        // Only collect actual spaces/rooms, not zone equipment
        // Also check that it's not already added and not a zone section
        if ((node.brick_class?.includes('Space') || node.brick_class?.includes('Room')) &&
            !node.brick_class?.includes('Zone') &&
            node.type !== 'equipment' &&
            node.subtype !== 'zone_section' &&
            !spaceIdSet.has(node.id)) {
          spaces.push(node);
          spaceIdSet.add(node.id);
        }
        if (node.children) {
          node.children.forEach((child) => collectSpaces(child));
        }
      };
      localHierarchyData.value.children.forEach((building) => collectSpaces(building));
    }

    // Link spaces to VAVs or directly to AHU
    spaces.forEach((space) => {
      // Check if this space has equipment that could be served by our AHU/VAVs
      const spaceEquipment = equipmentList.value.filter((eq) =>
        eq.location_space_id === space.id || eq.space_id === space.id,
      );

      // Only add spaces that have HVAC-related equipment or are marked as served
      const isServed = spaceEquipment.some((eq) =>
        eq.brick_class?.includes('Thermostat') ||
        eq.brick_class?.includes('Temperature_Sensor') ||
        eq.equipment_type === 'Thermostat' ||
        eq.served_by === ahuData.id ||
        vavs.some((vav) => eq.served_by === vav.id),
      );

      if (isServed || vavs.length === 0) { // Include all spaces if no VAVs
        // Extract the actual space name from the node structure
        const spaceName = space.label || space.name || space.data?.name || `Space ${space.id}`;
        nodes.push({
          id: space.id,
          name: spaceName,
          type: 'Space',
          brick_class: space.brick_class || space.brickClass,
        });
        const spaceIndex = nodes.length - 1;

        // Determine which equipment serves this space
        let sourceIndex = 0; // Default to AHU
        let airflowValue = 200; // Default airflow

        // Check if any zone serves this space
        if (zones.length > 0) {
          // Check for serves_space_id in zone metadata
          const servingZone = zones.find((zone) => {
            const servesSpaceId = zone.metadata?.serves_space_id || zone.serves_space_id;
            return servesSpaceId === space.id || servesSpaceId === space.data?.id;
          });

          if (servingZone) {
            sourceIndex = servingZone.index;
            // Space is served by zone
          }
        }

        // If not served by a zone, check if a VAV serves this space
        if (sourceIndex === 0 && vavs.length > 0) {
          const servingVav = vavs.find((vav) => {
            const vavEquipment = spaceEquipment.find((eq) =>
              eq.served_by === vav.id || eq.parent_equipment_id === vav.id,
            );
            return !!vavEquipment;
          });

          if (servingVav) {
            sourceIndex = nodeMap.get(servingVav.id);
          } else {
            // Assign to nearest VAV by ID if we have VAVs but no explicit connection
            sourceIndex = 1 + (space.id % vavs.length);
          }
        }

        // Get airflow value - ensure it's a number for proper proportions
        airflowValue = Number(space.airflow || space.cfm || space.data?.airflow || space.data?.cfm || 200);

        links.push({ source: sourceIndex, target: spaceIndex, value: airflowValue });
      }
    });

    // If no connections found, show message
    if (nodes.length === 1) {
      container.innerHTML = `
        <div class="text-center text-grey-6 q-pa-xl">
          <q-icon name="info" size="48px" color="grey-4" />
          <div class="text-h6 q-mt-md">No airflow connections found</div>
          <div class="text-body2">This AHU doesn't have any connected VAVs or spaces in the BRICK model.</div>
          <div class="text-caption q-mt-sm">Add VAVs and space relationships to visualize airflow distribution.</div>
        </div>
      `;
      return;
    }

    // Check if we have any links to display
    if (links.length === 0) {
      // No links found for Sankey diagram
      container.innerHTML = '<div class="text-center text-grey-6 q-pa-md">No airflow connections found for this AHU<br><small>No VAVs connected to this AHU in the database</small></div>';
      return;
    }

    // Remove orphaned nodes (nodes with no connections)
    const connectedNodeIds = new Set();
    links.forEach((link) => {
      // Links use indices, so we need to get the node IDs
      connectedNodeIds.add(nodes[link.source].id);
      connectedNodeIds.add(nodes[link.target].id);
    });

    // Filter nodes to only include connected ones
    const connectedNodes = nodes.filter((node) => connectedNodeIds.has(node.id));

    // Update links to use new indices after filtering
    const nodeIndexMap = new Map();
    connectedNodes.forEach((node, index) => {
      nodeIndexMap.set(node.id, index);
    });

    const updatedLinks = links.map((link) => ({
      source: nodeIndexMap.get(nodes[link.source].id),
      target: nodeIndexMap.get(nodes[link.target].id),
      value: Number(link.value), // Ensure value is a number for proper proportions
    }));

    // Creating Sankey diagram with nodes and links

    // Set dimensions based on connected nodes
    const margin = { top: 20, right: 120, bottom: 20, left: 120 };
    const width = container.clientWidth - margin.left - margin.right;
    const height = Math.max(400, connectedNodes.length * 40) - margin.top - margin.bottom;

    // Create SVG
    const svg = d3.select(container)
      .append('svg')
      .attr('width', width + margin.left + margin.right)
      .attr('height', height + margin.top + margin.bottom)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Create Sankey generator with proper configuration
    const sankeyGenerator = sankey()
      .nodeId((d) => d.id)
      .nodeWidth(20)
      .nodePadding(15)
      .extent([[0, 0], [width, height]]);

    // Generate the Sankey diagram with connected nodes only
    const sankeyData = sankeyGenerator({
      nodes: connectedNodes.map((d) => Object.assign({}, d)),
      links: updatedLinks.map((d) => Object.assign({}, d)),
    });

    // Color scale
    const color = d3.scaleOrdinal()
      .domain(['AHU', 'VAV', 'Zone', 'Space'])
      .range(['#1976d2', '#42a5f5', '#66bb6a', '#ffa726']);

    // Add links
    svg.append('g')
      .selectAll('.link')
      .data(sankeyData.links)
      .join('path')
      .attr('class', 'link')
      .attr('d', sankeyLinkHorizontal())
      .attr('stroke', (d) => color(d.source.type))
      .attr('stroke-width', (d) => Math.max(1, d.width))
      .attr('fill', 'none')
      .attr('opacity', 0.5)
      .append('title')
      .text((d) => `${d.source.name} → ${d.target.name}\nAirflow: ${d.value} l/s`);

    // Add nodes
    const node = svg.append('g')
      .selectAll('.node')
      .data(sankeyData.nodes)
      .join('g')
      .attr('class', 'node')
      .attr('transform', (d) => `translate(${d.x0},${d.y0})`);

    // Add rectangles for nodes
    node.append('rect')
      .attr('height', (d) => d.y1 - d.y0)
      .attr('width', sankeyGenerator.nodeWidth())
      .attr('fill', (d) => color(d.type))
      .attr('stroke', '#000')
      .attr('stroke-width', 1)
      .append('title')
      .text((d) => `${d.name}\nType: ${d.type}`);

    // Add labels
    node.append('text')
      .attr('x', (d) => d.x0 < width / 2 ? -6 : sankeyGenerator.nodeWidth() + 6)
      .attr('y', (d) => (d.y1 - d.y0) / 2)
      .attr('dy', '0.35em')
      .attr('text-anchor', (d) => d.x0 < width / 2 ? 'end' : 'start')
      .style('font-size', '12px')
      .text((d) => d.name);
  };

  return {
    renderSankeyDiagram,
  };
}
